/**
 * THE SEARCH BOX'S RULES, with no React and no DOM in them.
 *
 * Every decision a search box gets wrong is in this file rather than in the component, because they are all
 * decisions about MEANING and none of them is about rendering:
 *
 *   • WHEN A QUERY IS RUNNABLE AT ALL. The server refuses a query shorter than its minimum with a `400` —
 *     it declines to LOOK — and an empty list is the different claim that nothing matched. A client that
 *     sent the short query anyway would render the refusal as "no results", which is the one reading that
 *     makes a coverage gap invisible. So the minimum is enforced here, before a request exists.
 *   • WHAT THE USER IS LOOKING AT WHILE THEY TYPE. "Still typing", "we are asking", "nothing matches" and
 *     "the request failed" are four different states, and collapsing the first three into an empty panel is
 *     how a working search reads as a broken one. `phaseOf` derives them from the query the RESULT was for
 *     versus the query the box now holds — never from a loading flag alone, which goes false the instant a
 *     stale answer lands.
 *   • WHAT A PASTED ADDRESS OFFERS. The route answers an address with a token row, and it cannot answer for
 *     a WALLET — both are 32-byte base58 and only the caller knows which was meant. So an address-shaped
 *     query gets an extra option rather than a guess, and `optionsFor` is what makes the keyboard treat it
 *     as one more row instead of a special case in the component.
 *
 * ⚠️ THE THREE CONSTANTS ARE A SECOND COPY OF THE SERVER'S, and `tests/search.test.ts` READS
 * `packages/stores/src/search.ts` and asserts they are identical. The browser may not import that package
 * (it reaches Redis, ClickHouse, Postgres and the parsers through value imports) and `@kinetiq/wire` may not
 * carry them (its `typesOnly` gate pins its runtime exports to the closed vocabularies). So the copy is
 * CHECKED rather than trusted: a minimum raised in the store becomes a failing test here, not a box that
 * sends a query the server has started refusing.
 */

import type { TokenRow } from "@kinetiq/wire";
import { looksLikeAddress } from "../../lib/address.ts";

/** Below this, the server refuses to look. */
export const SEARCH_QUERY_MIN = 2;
/** Longer queries are truncated — by the server too, so the box shows what will actually be run. */
export const SEARCH_QUERY_MAX = 64;
/**
 * The route's own ceiling — asking for more is CLAMPED to this, silently, and is not an error.
 *
 * MEASURED against the running server: `?limit=999` answers `200` with 50 rows; only a NON-NUMERIC limit is
 * a `400`. So a caller that asks for 200 and reads 50 has been given the ceiling, not a partial page, and
 * must not treat the short list as "that is all there was" — `matched` on the response is the number that
 * says how many there really were.
 */
export const SEARCH_LIMIT_MAX = 50;

/**
 * HOW MANY ROWS THE PANEL ASKS FOR.
 *
 * A dropdown is not a board: the ranking's whole job is to put the right token first, and a user who has to
 * scroll a panel to find it has been failed by the ranking rather than by the page size. Twelve is a
 * screenful without one. `matched` on the response says how many there were in total, and the panel prints
 * that — so the cut is stated rather than hidden behind a list that simply ends.
 */
export const SEARCH_PAGE = 12;

/**
 * THE DEBOUNCE, and what it is actually protecting.
 *
 * NOT the rate limiter: `/v1/search` is in the general bucket (120 burst, 20/s) and a human cannot type fast
 * enough to trip it. What a request per keystroke spends is a CACHE MISS per prefix — and a miss is what
 * offers every mint it returned to enrichment — so "bonk" typed one letter at a time enriches for `bo`, `bon`
 * and `bonk` when the user only ever meant the last one. 200 ms is inside the interval between keystrokes for
 * a touch typist and below the ~250 ms at which a person reads a pause as lag.
 */
export const SEARCH_DEBOUNCE_MS = 200;

/**
 * TRIM, COLLAPSE, BOUND — the same three operations the server's `normalizeQuery` performs, in the same
 * order, so the string the box shows as "what is being searched" is the string the response echoes.
 *
 * CASE IS PRESERVED, and that is load-bearing rather than incidental: a mint address is base58 and
 * case-significant, so a normaliser that lowercased would destroy the one query shape that must survive
 * verbatim. The server lowercases internally for TEXT matching only.
 */
export function normaliseQuery(raw: string): string {
  return raw.trim().replace(/\s+/g, " ").slice(0, SEARCH_QUERY_MAX);
}

/** Is this long enough for the server to look at? The one gate, so no other module has to remember it. */
export const isRunnable = (query: string): boolean => query.length >= SEARCH_QUERY_MIN;

/** What the panel is showing right now. Four states, because they are four different facts. */
export type SearchPhase =
  /** Nothing typed. The panel is closed — an empty panel over an empty box is noise. */
  | { readonly kind: "idle" }
  /** Typed, but shorter than the server's minimum. NOT a result: nothing has been asked. */
  | { readonly kind: "tooShort"; readonly need: number }
  /** A request is scheduled or in flight FOR THE QUERY NOW IN THE BOX. Anything held is for an older one. */
  | { readonly kind: "pending" }
  /** An answer, for this exact query, with rows. */
  | {
      readonly kind: "results";
      readonly options: readonly SearchOption[];
      readonly result: SearchResult;
    }
  /** An answer, for this exact query, with NO rows. This is the only state that means "nothing matches". */
  | {
      readonly kind: "empty";
      readonly result: SearchResult;
      readonly options: readonly SearchOption[];
    }
  /** The request failed. Distinct from empty, always. */
  | { readonly kind: "error"; readonly message: string };

/** One answer, tagged with the query it answers — which is what lets `phaseOf` tell a current result from a
 *  stale one without a second flag to keep in step. */
export interface SearchResult {
  readonly query: string;
  readonly count: number;
  readonly matched: number;
  readonly scored: number;
  readonly identityPending: boolean;
  readonly tokens: readonly TokenRow[];
}

/** A row of the panel, keyboard-navigable. The wallet option is an OPTION and not a button beside the list,
 *  so Down/Up reach it and Enter opens it like anything else. */
export type SearchOption =
  | { readonly kind: "token"; readonly mint: string; readonly row: TokenRow | null }
  | { readonly kind: "wallet"; readonly owner: string };

/** Where selecting an option goes. The one place a destination is spelled, so the click path and the Enter
 *  path cannot disagree about where a row leads. */
export function hrefForOption(o: SearchOption): string {
  return o.kind === "wallet"
    ? `#/wallet?owner=${encodeURIComponent(o.owner)}`
    : `#/token/${o.mint}`;
}

/**
 * THE PANEL'S ROWS, from the answer plus the query that produced it.
 *
 * An address-shaped query gains a WALLET option, always — including when the token lookup returned a row,
 * because a mint that is also a wallet address is not the case being handled: the route cannot tell them
 * apart, so the user is offered both destinations rather than sent to whichever one the server happened to
 * find. It is LAST: a query that resolved to a real token is far more often a token.
 */
export function optionsFor(result: SearchResult): readonly SearchOption[] {
  const options: SearchOption[] = result.tokens.map((row) => ({
    kind: "token",
    mint: row.mint,
    row,
  }));
  if (looksLikeAddress(result.query)) options.push({ kind: "wallet", owner: result.query });
  return options;
}

/**
 * A PASTED ADDRESS THAT IS NOT A TOKEN WE TRACK — a wallet, or a program.
 *
 * ⚠️ THIS IS THE SECOND HALF OF A TWO-PHASE ANSWER, and reading only the first half is what makes it wrong.
 * MEASURED on the live serve, on one address, in this order:
 *
 *   1. FIRST LOOK — `identityPending: true` with an all-null row. So does a real mint we have not enriched
 *      yet: the field is "we hold no identity ROW", and the row is absent in both cases. At this point the
 *      two are INDISTINGUISHABLE and the panel must say so rather than pick one.
 *   2. AFTER the enrichment that very search queued — an identity row EXISTS and is empty, so
 *      `identityPending` flips to `false` while the name and symbol stay null. THAT combination is the
 *      answer: enrichment looked, and there was nothing to find.
 *
 * So it is `identityPending === false` AND a null identity AND an address query. A text query can never
 * reach it — a symbol match requires an identity to have matched.
 */
export function isUnknownAddress(result: SearchResult): boolean {
  if (result.identityPending) return false;
  if (!looksLikeAddress(result.query)) return false;
  const only = result.tokens.length === 1 ? result.tokens[0] : undefined;
  return only !== undefined && only.symbol === null && only.name === null;
}

export interface PhaseInput {
  /** The box's contents, already normalised. */
  readonly query: string;
  /** The newest answer we hold, whatever query it was for. */
  readonly result: SearchResult | null;
  /** A failure, tagged with the query it was for — a stale error must not sit over a fresh query. */
  readonly error: { readonly query: string; readonly message: string } | null;
}

/**
 * WHICH OF THE SIX STATES THE BOX IS IN.
 *
 * THE TEST IS `result.query === query`, and it is the whole point of the function. A box that decided
 * "empty" from `tokens.length === 0` renders the PREVIOUS query's emptiness under the current one — so
 * typing `b`, `bo`, `bon` shows "no matches for bon" using the answer for `bo`, and a search that is
 * working reads as one that is broken. Anything we hold that is not for THIS query is not an answer to it.
 */
export function phaseOf(input: PhaseInput): SearchPhase {
  const { query, result, error } = input;
  if (query === "") return { kind: "idle" };
  if (!isRunnable(query)) return { kind: "tooShort", need: SEARCH_QUERY_MIN - query.length };
  if (error !== null && error.query === query) return { kind: "error", message: error.message };
  if (result === null || result.query !== query) return { kind: "pending" };
  const options = optionsFor(result);
  // A pasted address with no token row still has a wallet option, so the panel is not EMPTY — it holds one
  // thing you can do. Deciding emptiness on the options rather than on the token count is what keeps the
  // "nothing matches" line off a panel that is offering a destination.
  if (options.length === 0) return { kind: "empty", result, options };
  return { kind: "results", options, result };
}

/**
 * ARROW KEYS OVER A LIST THAT CHANGES UNDER THE CURSOR.
 *
 * IT WRAPS, because a list this short has no scroll position to lose and Up from the first row meaning "the
 * last row" is what every combobox on the platform does. `count === 0` returns 0 rather than -1: there is
 * nothing to be active on, and a -1 would then have to be handled at the two call sites that index with it.
 */
export function nextActive(active: number, count: number, key: "up" | "down"): number {
  if (count <= 0) return 0;
  const clamped = Math.min(Math.max(active, 0), count - 1);
  return key === "down" ? (clamped + 1) % count : (clamped + count - 1) % count;
}

/**
 * WHAT THE PANEL SAYS UNDER THE ROWS, or `null` when it has nothing to add.
 *
 * TWO SEPARATE HONESTY CLAIMS, and neither is inferrable from the rows:
 *
 *   `matched > count`  — there are more matches than this page holds.
 *   `scored < matched` — the page was ranked over the best `scored` BY VOLUME ALONE, because pricing every
 *                        match of a two-character query is tens of milliseconds of synchronous work on the
 *                        loop that holds the fold. A token whose only trust is DEPTH — real liquidity, no
 *                        volume in the window — can be missing from a page that says this.
 */
export function coverageNote(result: SearchResult): string | null {
  const more = result.matched > result.count;
  const approximate = result.scored < result.matched;
  if (!more && !approximate) return null;
  const head = more ? `${result.count} of ${result.matched} matches` : `${result.count} matches`;
  return approximate ? `${head} · ranked over the top ${result.scored} by volume` : head;
}
